import {
  Accordion,
  AccordionData,
  AttachmentBox,
  AttachmentBoxSize,
  AttachmentBoxTheme,
  StepsPath,
  Typography,
  TypographyElement,
  TypographyVariant,
  UseState,
} from '@elearning-platform/ui';
import React, { useContext, useEffect, useState } from 'react';

import { CenteredContentSlideWrapper } from '..';
import { PageDataContext } from '../../components/page';
import { PageData } from '../../interfaces';

import { AccordionsWrapper, DescriptionContainer, StyledDescription } from './centeredContentSlide.styled';
import { CenteredContentSlideProps } from './centeredContentSlide.types';

export const CenteredContentSlide: React.FC<CenteredContentSlideProps> = (props: CenteredContentSlideProps): JSX.Element => {
  const { baseUrl, data, completed, callComplete }: CenteredContentSlideProps = props;
  const dataCtx: Partial<PageData> | null = useContext(PageDataContext);
  const {
    slidesOverallCounter,
    currentSlideCounter,
    title,
    description,
  }: Partial<PageData> = dataCtx ?? {};
  const [openedAccordions, setOpenedAccordions]: UseState<Set<number>> = useState<Set<number>>(new Set());

  useEffect((): void => {
    if (!completed) {
      const canComplete: boolean = !data.requireAllAccordionsOpened
        || !data.accordions
        || data.accordions.length === openedAccordions.size;

      if (canComplete) {
        callComplete();
      }
    }
  }, [completed, callComplete, data.requireAllAccordionsOpened, data.accordions, openedAccordions]);

  return (
    <CenteredContentSlideWrapper
      currentSlideCounter={currentSlideCounter}
      description={description}
      fullWidth={data.layoutSettings?.isFullWidth}
      leftImageSrc={data?.leftImageSrc ? baseUrl + data.leftImageSrc : undefined}
      rightImageSrc={data?.rightImageSrc ? baseUrl + data.rightImageSrc : undefined}
      slidesOverallCounter={slidesOverallCounter}
      title={title}
    >
      {!!data?.accordions && (
        <AccordionsWrapper $isTwoColumns={!!data?.layoutSettings?.isAccordionGridLayout}>
          {data.accordions.map((accordion: AccordionData, index: number): JSX.Element => (
            <Accordion
              buttonLabel={accordion.buttonLabel}
              content={accordion.content}
              headline={accordion.headline}
              // eslint-disable-next-line react/no-array-index-key
              key={`${accordion.headline}_${index}`}
              onOpen={(): void => setOpenedAccordions((prev: Set<number>): Set<number> => new Set(prev).add(index))}
            />
          ))}
        </AccordionsWrapper>
      )}
      {!!data?.steps && (
        <StepsPath steps={data.steps} />
      )}
      {!!data?.secondaryDescription && (
        <DescriptionContainer>
          <Typography tag={TypographyElement.div} variant={TypographyVariant.TextLG}>
            <StyledDescription text={data.secondaryDescription} />
          </Typography>
        </DescriptionContainer>
      )}
      {!!data?.attachmentBoxData && (
        <AttachmentBox
          basicText={data.attachmentBoxData.basicText}
          externalFileName={data.attachmentBoxData.externalFileName}
          externalFilePath={baseUrl + data.attachmentBoxData.externalFilePath}
          shouldOpenInNewTab
          size={AttachmentBoxSize.Large}
          supportingText={data.attachmentBoxData.supportingText}
          theme={AttachmentBoxTheme.Dark}
        />
      )}
    </CenteredContentSlideWrapper>
  );
};
